import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  StickyNote, Plus, Trash2, Pin, PinOff, X, CheckSquare, FileText,
  ChevronDown, ChevronUp, Loader2, GripVertical
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const NotebookDrawer = () => {
  const { activeAccountId } = useAuth();
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('todas'); // 'todas', 'pendentes', 'concluidas'
  const [expandedNotes, setExpandedNotes] = useState(new Set());

  // Contar pendências para o badge
  const pendingCount = notes.filter(n => {
    if (n.tipo === 'checklist') {
      const itens = Array.isArray(n.itens) ? n.itens : [];
      return itens.some(item => !item.done);
    }
    return !n.concluido;
  }).length;

  // ── Fetch ──
  const loadNotes = useCallback(async () => {
    if (!activeAccountId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_notes')
        .select('*')
        .eq('user_id', activeAccountId)
        .order('fixado', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      console.error('Erro ao carregar notas:', err);
    } finally {
      setLoading(false);
    }
  }, [activeAccountId]);

  useEffect(() => {
    if (isOpen) loadNotes();
  }, [isOpen, loadNotes]);

  // ── Create ──
  const handleCreateNote = async (tipo) => {
    if (!activeAccountId) return;

    const newNote = {
      user_id: activeAccountId,
      tipo,
      titulo: tipo === 'nota' ? 'Nova anotação' : 'Nova checklist',
      conteudo: '',
      itens: tipo === 'checklist' ? [{ id: Date.now(), text: '', done: false }] : [],
      fixado: false,
      concluido: false,
      ordem: 0,
    };

    const { data, error } = await supabase.from('user_notes').insert(newNote).select();
    if (error) {
      toast({ title: "Erro", description: "Falha ao criar anotação.", variant: "destructive" });
      return;
    }
    if (data && data[0]) {
      setNotes(prev => [data[0], ...prev]);
      setExpandedNotes(prev => new Set(prev).add(data[0].id));
    }
  };

  // ── Update field ──
  const updateNote = async (id, updates) => {
    setNotes(prev => prev.map(n => n.id === id ? { ...n, ...updates } : n));

    const { error } = await supabase.from('user_notes').update(updates).eq('id', id);
    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
      loadNotes(); // rollback
    }
  };

  // Debounced text update
  const handleTextChange = useCallback((id, field, value) => {
    setNotes(prev => prev.map(n => n.id === id ? { ...n, [field]: value } : n));
  }, []);

  const handleTextBlur = async (id, field, value) => {
    await supabase.from('user_notes').update({ [field]: value }).eq('id', id);
  };

  // ── Toggle Pin ──
  const togglePin = async (note) => {
    await updateNote(note.id, { fixado: !note.fixado });
    // Re-sort after pin change
    setTimeout(loadNotes, 200);
  };

  // ── Toggle Concluído ──
  const toggleConcluido = async (note) => {
    await updateNote(note.id, { concluido: !note.concluido });
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    setNotes(prev => prev.filter(n => n.id !== id));
    const { error } = await supabase.from('user_notes').delete().eq('id', id);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
      loadNotes();
    }
  };

  // ── Checklist item ops ──
  const updateChecklistItems = async (noteId, newItems) => {
    setNotes(prev => prev.map(n => n.id === noteId ? { ...n, itens: newItems } : n));
    await supabase.from('user_notes').update({ itens: newItems }).eq('id', noteId);
  };

  const toggleCheckItem = (noteId, itemId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    const newItems = note.itens.map(item =>
      item.id === itemId ? { ...item, done: !item.done } : item
    );
    updateChecklistItems(noteId, newItems);
  };

  const updateCheckItemText = (noteId, itemId, text) => {
    setNotes(prev => prev.map(n => {
      if (n.id !== noteId) return n;
      return { ...n, itens: n.itens.map(item => item.id === itemId ? { ...item, text } : item) };
    }));
  };

  const saveCheckItemText = async (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    await supabase.from('user_notes').update({ itens: note.itens }).eq('id', noteId);
  };

  const addCheckItem = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    const newItems = [...note.itens, { id: Date.now(), text: '', done: false }];
    updateChecklistItems(noteId, newItems);
  };

  const removeCheckItem = (noteId, itemId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    const newItems = note.itens.filter(item => item.id !== itemId);
    updateChecklistItems(noteId, newItems);
  };

  // ── Toggle expand ──
  const toggleExpand = (id) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Filter ──
  const filteredNotes = notes.filter(note => {
    if (filter === 'todas') return true;
    if (filter === 'pendentes') {
      if (note.tipo === 'checklist') {
        const itens = Array.isArray(note.itens) ? note.itens : [];
        return itens.some(item => !item.done);
      }
      return !note.concluido;
    }
    if (filter === 'concluidas') {
      if (note.tipo === 'checklist') {
        const itens = Array.isArray(note.itens) ? note.itens : [];
        return itens.length > 0 && itens.every(item => item.done);
      }
      return note.concluido;
    }
    return true;
  });

  // ── Render ──
  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/30 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group"
        title="Caderno de Bordo"
      >
        <StickyNote className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center animate-pulse">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#0b1727] border-l border-white/10 z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <StickyNote className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Caderno de Bordo</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Actions Bar */}
        <div className="p-3 border-b border-white/10 flex items-center gap-2 shrink-0">
          <Button
            onClick={() => handleCreateNote('nota')}
            size="sm"
            className="bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30 gap-1.5 flex-1"
          >
            <FileText className="w-4 h-4" />
            Nova Nota
          </Button>
          <Button
            onClick={() => handleCreateNote('checklist')}
            size="sm"
            className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30 gap-1.5 flex-1"
          >
            <CheckSquare className="w-4 h-4" />
            Nova Checklist
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="px-3 pt-3 pb-2 flex gap-1 shrink-0">
          {[
            { key: 'todas', label: 'Todas' },
            { key: 'pendentes', label: 'Pendentes' },
            { key: 'concluidas', label: 'Concluídas' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  : 'text-white/50 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="text-center py-12 text-white/30">
              <StickyNote className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nenhuma anotação encontrada.</p>
              <p className="text-xs mt-1">Clique em "Nova Nota" ou "Nova Checklist" para começar.</p>
            </div>
          ) : (
            filteredNotes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                expanded={expandedNotes.has(note.id)}
                onToggleExpand={() => toggleExpand(note.id)}
                onTogglePin={() => togglePin(note)}
                onToggleConcluido={() => toggleConcluido(note)}
                onDelete={() => handleDelete(note.id)}
                onTextChange={handleTextChange}
                onTextBlur={handleTextBlur}
                onToggleCheckItem={(itemId) => toggleCheckItem(note.id, itemId)}
                onUpdateCheckItemText={(itemId, text) => updateCheckItemText(note.id, itemId, text)}
                onSaveCheckItemText={() => saveCheckItemText(note.id)}
                onAddCheckItem={() => addCheckItem(note.id)}
                onRemoveCheckItem={(itemId) => removeCheckItem(note.id, itemId)}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

// ── Note Card Component ──
const NoteCard = ({
  note, expanded, onToggleExpand, onTogglePin, onToggleConcluido, onDelete,
  onTextChange, onTextBlur,
  onToggleCheckItem, onUpdateCheckItemText, onSaveCheckItemText, onAddCheckItem, onRemoveCheckItem
}) => {
  const isNota = note.tipo === 'nota';
  const itens = Array.isArray(note.itens) ? note.itens : [];
  const doneCount = itens.filter(i => i.done).length;
  const totalCount = itens.length;
  const allDone = totalCount > 0 && doneCount === totalCount;

  const borderColor = isNota
    ? (note.concluido ? 'border-white/5' : 'border-blue-500/30')
    : (allDone ? 'border-white/5' : 'border-emerald-500/30');

  const headerBg = isNota
    ? (note.concluido ? 'bg-white/5' : 'bg-blue-500/10')
    : (allDone ? 'bg-white/5' : 'bg-emerald-500/10');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`rounded-lg border ${borderColor} overflow-hidden transition-all duration-200 ${(note.concluido || allDone) ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div className={`${headerBg} px-3 py-2 flex items-center gap-2 cursor-pointer`} onClick={onToggleExpand}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isNota
            ? <FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" />
            : <CheckSquare className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          }
          {note.fixado && <Pin className="w-3 h-3 text-amber-400 shrink-0" />}
          {expanded ? (
            <input
              type="text"
              value={note.titulo}
              onChange={(e) => onTextChange(note.id, 'titulo', e.target.value)}
              onBlur={(e) => onTextBlur(note.id, 'titulo', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="bg-transparent text-white text-sm font-medium border-none outline-none w-full"
              placeholder="Título da nota..."
            />
          ) : (
            <span className={`text-sm font-medium text-white truncate ${(note.concluido || allDone) ? 'line-through' : ''}`}>
              {note.titulo || 'Sem título'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isNota && totalCount > 0 && (
            <span className="text-[10px] text-white/40 mr-1">{doneCount}/{totalCount}</span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="p-3 space-y-3">
          {/* Nota: textarea */}
          {isNota && (
            <>
              <textarea
                value={note.conteudo}
                onChange={(e) => onTextChange(note.id, 'conteudo', e.target.value)}
                onBlur={(e) => onTextBlur(note.id, 'conteudo', e.target.value)}
                placeholder="Escreva sua anotação aqui..."
                className="w-full bg-white/5 border border-white/10 rounded-md p-2.5 text-sm text-white placeholder:text-white/20 resize-none outline-none focus:border-blue-500/50 transition-colors min-h-[100px]"
                rows={4}
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={note.concluido}
                  onCheckedChange={onToggleConcluido}
                  className="border-white/20"
                />
                <span className="text-xs text-white/40">Marcar como concluída</span>
              </div>
            </>
          )}

          {/* Checklist: items */}
          {!isNota && (
            <div className="space-y-1.5">
              {itens.map((item, index) => (
                <div key={item.id} className="flex items-center gap-2 group">
                  <Checkbox
                    checked={item.done}
                    onCheckedChange={() => onToggleCheckItem(item.id)}
                    className="border-white/20 shrink-0"
                  />
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => onUpdateCheckItemText(item.id, e.target.value)}
                    onBlur={() => onSaveCheckItemText()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onAddCheckItem();
                      }
                    }}
                    placeholder={`Item ${index + 1}...`}
                    className={`bg-transparent border-none outline-none text-sm flex-1 ${
                      item.done ? 'line-through text-white/30' : 'text-white placeholder:text-white/20'
                    }`}
                  />
                  <button
                    onClick={() => onRemoveCheckItem(item.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-400/50 hover:text-red-400 transition-opacity"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                onClick={onAddCheckItem}
                className="flex items-center gap-1.5 text-xs text-white/30 hover:text-emerald-400 transition-colors pt-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Adicionar item
              </button>
            </div>
          )}

          {/* Footer: actions + date */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <span className="text-[10px] text-white/20">{formatDate(note.updated_at)}</span>
            <div className="flex items-center gap-1">
              <button
                onClick={onTogglePin}
                className={`p-1.5 rounded hover:bg-white/10 transition-colors ${note.fixado ? 'text-amber-400' : 'text-white/30 hover:text-white/50'}`}
                title={note.fixado ? 'Desafixar' : 'Fixar no topo'}
              >
                {note.fixado ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-white/10 transition-colors" title="Excluir nota">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0b1727] border-white/10 text-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir esta anotação?</AlertDialogTitle>
                    <AlertDialogDescription className="text-white/70">
                      A anotação "{note.titulo}" será excluída permanentemente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/10 text-white hover:bg-white/20 border-white/20">Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotebookDrawer;
